import React from "react";
import { IStringLocalizer } from "../../../utilities/IStringLocalizer";
import AIAnswerMetadataConfidenceBadge from "./AIAnswerMetadataConfidenceBadge";
import AIAnswerMetadataRevertButton from "./AIAnswerMetadataRevertButton";
import AIAnswerMetadataHelpfulActions from "./AIAnswerMetadataHelpfulActions";
import { HelpfulSelection } from "./aiAnswerMetadata.types";

const chevronDownIconSrc = "/images/ai-answer-metadata/chevron-down-icon.svg";
const chevronUpIconSrc = "/images/ai-answer-metadata/chevron-up-icon.svg";
const modifiedIconSrc = "/images/ai-answer-metadata/modified-icon.svg";
const suggestedIconSrc = "/images/ai-answer-metadata/suggested-icon.svg";

interface AIAnswerMetadataHeaderProps {
    localizer: IStringLocalizer;
    isModifiedValue: boolean;
    isConfirmed: boolean;
    isDisabled: boolean;
    isExpanded: boolean;
    isDetailsHighlighted: boolean;
    confidenceLabel: string;
    shouldShowRevertButton?: boolean;
    helpfulSelection: HelpfulSelection;
    onToggleDetails: () => void;
    onRevertClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onHelpfulClick: (selection: -1 | 1, event: React.MouseEvent<HTMLButtonElement>) => void;
}

const AIAnswerMetadataHeader: React.FC<AIAnswerMetadataHeaderProps> = ({
    localizer,
    isModifiedValue,
    isConfirmed,
    isDisabled,
    isExpanded,
    isDetailsHighlighted,
    confidenceLabel,
    shouldShowRevertButton = true,
    helpfulSelection,
    onToggleDetails,
    onRevertClick,
    onHelpfulClick
}) => (
    <div className="ai-answer-field__header">
        <div className="ai-answer-field__header-left">
            {isModifiedValue
                ? <img src={modifiedIconSrc} className="ai-answer-field__modified-icon" aria-hidden="true" alt="" />
                : isConfirmed
                    ? <span className="glyphicon glyphicon-thumbs-up ai-answer-field__confirmed-icon" aria-hidden="true"></span>
                    : <img src={suggestedIconSrc} className="ai-answer-field__suggested-icon" aria-hidden="true" alt="" />
            }
            <span className="ai-answer-field__title">
                {isModifiedValue
                    ? localizer.localizeString("Modified")
                    : isConfirmed
                        ? localizer.localizeString("Confirmed")
                        : localizer.localizeString("Suggested Answer")}
            </span>
            {/* Confirmed answers intentionally hide the confidence pill; confidence is relevant only while suggested. */}
            {!isModifiedValue && !isConfirmed &&
                <AIAnswerMetadataConfidenceBadge
                    localizer={localizer}
                    confidenceLabel={confidenceLabel}
                />
            }
        </div>
        <div className="ai-answer-field__header-actions">
            <button
                type="button"
                className={isDetailsHighlighted
                    ? "btn btn-link btn-xs ai-answer-field__details-button ai-answer-field__details-button--flash"
                    : "btn btn-link btn-xs ai-answer-field__details-button"}
                onClick={onToggleDetails}
                aria-expanded={isExpanded}
                aria-disabled={isDisabled}
                disabled={isDisabled}
            >
                {localizer.localizeString("Details")}
                {isExpanded
                    ? <img src={chevronUpIconSrc} className="ai-answer-field__details-icon" aria-hidden="true" alt="" />
                    : <img src={chevronDownIconSrc} className="ai-answer-field__details-icon" aria-hidden="true" alt="" />
                }
            </button>
            {shouldShowRevertButton &&
                <AIAnswerMetadataRevertButton
                    localizer={localizer}
                    isModifiedValue={isModifiedValue}
                    isDisabled={isDisabled}
                    onRevertClick={onRevertClick}
                />
            }
            <AIAnswerMetadataHelpfulActions
                localizer={localizer}
                isDisabled={isDisabled}
                helpfulSelection={helpfulSelection}
                onHelpfulClick={onHelpfulClick}
            />
        </div>
    </div>
);

export default AIAnswerMetadataHeader;
